/**
 * nativeBridge — single source of truth for "are we inside the
 * native iOS/Android Capacitor shell or a regular web browser?"
 *
 * The whole Armstrong K9 Registry frontend is one React build. The
 * exact same JS runs on the web AND inside the Capacitor app. This
 * helper lets us swap behaviour just where the App Store rules
 * demand it (no in-app purchase buttons, swap "Buy" CTAs for "Sign
 * in to access your web subscription", etc.).
 *
 * Capacitor injects `window.Capacitor` when running inside the
 * native shell. We check that — no extra SDK needed for the web
 * build to remain lean.
 */

/** True when this React app is running inside the Capacitor wrapper
 *  (iOS or Android). False on the regular website. */
export function isNativeApp() {
  if (typeof window === "undefined") return false;
  if (process.env.NODE_ENV === "development" && window.localStorage?.getItem("emergent_force_native") === "1") return true;
  return Boolean(window.Capacitor?.isNativePlatform?.());
}

/** Open a URL in the system browser (SFSafariViewController / Chrome
 *  Custom Tab) on native, or a new tab on web. Use for Stripe Connect
 *  onboarding and any external site the WebView shouldn't swallow. */
export async function openExternal(url) {
  if (isNativeApp()) {
    try {
      const { Browser } = await import("@capacitor/browser");
      await Browser.open({ url, presentationStyle: "popover" });
      return;
    } catch {
      /* plugin missing in old binaries — fall through */
    }
  }
  window.open(url, "_blank", "noopener");
}

/** The real https web origin of the app. Inside the Capacitor shell
 *  `window.location.origin` is `capacitor://armstrong-k9.com` (or the
 *  fake https hostname) — useless for image URLs, embed snippets,
 *  share links and Stripe redirects. On native we derive the origin
 *  from REACT_APP_BACKEND_URL (the deployed site); on web we pass
 *  through the browser's own origin. */
export function webOrigin() {
  if (isNativeApp()) return new URL(process.env.REACT_APP_BACKEND_URL).origin;
  return window.location.origin;
}

/** Returns "ios" | "android" | "web". */
export function getPlatform() {
  if (typeof window === "undefined") return "web";
  if (process.env.NODE_ENV === "development" && window.localStorage?.getItem("emergent_force_native") === "1") return "ios";
  return window.Capacitor?.getPlatform?.() || "web";
}

/** True only on iOS — Apple is stricter than Google about external
 *  links / non-IAP purchase flows. Some surfaces use this to render
 *  even-more-conservative copy. */
export function isIOS() {
  return getPlatform() === "ios";
}
